import { faPencil, faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dialog, InputAdornment, TextField } from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Props, State } from "./interfaces";
import { ActionButton, DialogContent } from "./styles";
import { useAppSelector } from "../../../../../../custom-hooks/redux/redux";
import {
  VendorInvoiceRef,
  VendorInvoiceVATBaseRef,
} from "../../../../../../models/legal-entity-vendors/vendor-invoices";
import DialogActions from "../../../../../DialogActions/DialogActions";
import DialogTitle from "../../../../../DialogTitle/DialogTitle";

const DEFAULT_VAT_BASE: VendorInvoiceVATBaseRef = {
  id: "",
  isDeleted: false,
  companyAccount: null,
  vendorInvoice: null,
  base: 0,
  rate: 0,
  amount: 0,
};

const AddEditVatBaseRow = ({ handleChangeVat, vat, vendorInvoice }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_transaction.transaction_screen_two.vat_base"; // tRP - translation root path

  const currCompanyAccount = useAppSelector((state) => state.currentUserReducer.currentCompanyAccount);

  const [state, setState] = useState<State>({
    openDialog: false,
    vat: { ...DEFAULT_VAT_BASE },
  });

  const setOpenDialog = (value: boolean) =>
    setState((state) => ({
      ...state,
      openDialog: value,
      vat: vat ?? DEFAULT_VAT_BASE,
    }));

  const handleCloseDialog = () =>
    setState((state) => ({
      ...state,
      openDialog: false,
    }));

  const handleChangeVatField = (
    field: keyof VendorInvoiceVATBaseRef,
    value: VendorInvoiceVATBaseRef[keyof VendorInvoiceVATBaseRef]
  ) => {
    let computedAmount = state.vat.amount;

    if (field === "base") {
      computedAmount = +((+(value as number) * state.vat.rate) / 100).toFixed(2);
    }
    if (field === "rate") {
      computedAmount = +((+(value as number) * state.vat.base) / 100).toFixed(2);
    }
    if (field === "amount") {
      computedAmount = +(value as number);
    }
    setState((state) => ({
      ...state,
      vat: { ...state.vat, [field]: value, amount: computedAmount },
    }));
  };

  const handleConfirm = () => {
    handleChangeVat({
      ...state.vat,
      id: vat ? vat.id : uuidv4(),
      companyAccount: vat ? vat.companyAccount : currCompanyAccount,
      vendorInvoice: vat
        ? ({ id: vat.vendorInvoice?.id } as VendorInvoiceRef)
        : ({ id: vendorInvoice.id } as VendorInvoiceRef),
    });
    setState((state) => ({
      ...state,
      openDialog: false,
      vat: { ...DEFAULT_VAT_BASE },
    }));
  };

  const setInitialLoan = useCallback(() => {
    setState((state) => ({
      ...state,
      vat: {
        ...state.vat,
        ...vat,
        companyAccount: currCompanyAccount,
      },
    }));
  }, [vat]);

  useEffect(() => {
    setInitialLoan();
    return () => {
      setState({
        openDialog: false,
        vat: { ...DEFAULT_VAT_BASE },
      });
    };
  }, [setInitialLoan]);

  return (
    <>
      {vat ? (
        <ActionButton onClick={() => setOpenDialog(true)}>
          <FontAwesomeIcon icon={faPencil} />
        </ActionButton>
      ) : (
        <Button onClick={() => setOpenDialog(true)}>
          <FontAwesomeIcon
            icon={faPlusCircle}
            style={{ width: "25px", height: "25px" }}
          />
        </Button>
      )}
      <Dialog
        open={state.openDialog}
        onClose={handleCloseDialog}
        fullWidth
      >
        <DialogTitle
          title={vat ? t(`${tRP}.edit_title`) : t(`${tRP}.title`)}
          handleClose={handleCloseDialog}
        />
        <DialogContent>
          <TextField
            label={`${t(`${tRP}.fields.base`)}`}
            type="number"
            InputProps={{
              inputProps: { step: 0.01 },
              endAdornment: <InputAdornment position="end">{vendorInvoice?.currency?.code}</InputAdornment>,
            }}
            value={state.vat.base}
            onChange={(e) => handleChangeVatField("base", e.target.value)}
          />
          <TextField
            label={`${t(`${tRP}.fields.vat_type`)}`}
            type="number"
            InputProps={{
              inputProps: { step: 0.1 },
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            value={state.vat.rate}
            onChange={(e) => handleChangeVatField("rate", e.target.value)}
          />
          <TextField
            label={`${t(`${tRP}.fields.amount`)}`}
            type="number"
            InputProps={{
              inputProps: { step: 0.01 },
              endAdornment: <InputAdornment position="end">{vendorInvoice?.currency?.code}</InputAdornment>,
            }}
            value={state.vat.amount}
            onChange={(e) => handleChangeVatField("amount", e.target.value)}
          />
        </DialogContent>
        <DialogActions
          handleCancel={handleCloseDialog}
          handleSave={handleConfirm}
          customSaveName={`${t("buttons.confirm")}`}
        />
      </Dialog>
    </>
  );
};

export default AddEditVatBaseRow;
